import { useState, useEffect, lazy, Suspense } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, LogIn } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";

// LoginModal drags supabase-js, sonner, react-hook-form-less form UI etc.
// Load it only after the user clicks "access".
const LoginModal = lazy(() =>
  import("@/components/portal/LoginModal").then((m) => ({ default: m.LoginModal }))
);

const Navbar = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);

  const navLinks: [string, string][] = [
    [t("landing.navProblem"), "problema"],
    [t("landing.navSolution"), "solucion"],
    [t("landing.v2.navPlatform"), "plataforma"],
    [t("landing.v2.navImpact"), "impacto"],
    [t("landing.v2.navGlobal"), "global"],
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setIsOpen(false);
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${scrolled ? 'bg-background/70 backdrop-blur-2xl border-b border-border/30' : 'bg-transparent'}`}>
        <div className="container mx-auto px-4 flex items-center justify-between h-18 py-3">
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-3 group">
            <span className="font-display text-lg font-bold text-foreground tracking-tight">SBDR</span>
          </button>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(([label, id]) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className="nav-link-underline text-muted-foreground hover:text-foreground text-xs font-mono uppercase tracking-widest transition-colors duration-300"
              >
                {label}
              </button>
            ))}
            <button
              onClick={() => setLoginOpen(true)}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-xs font-mono uppercase tracking-widest transition-colors duration-300"
            >
              <LogIn className="h-3.5 w-3.5" />
              {t("landing.navAccess")}
            </button>
            <LanguageSwitcher variant="landing" />
            <Button variant="hero" size="sm" className="shimmer-btn" onClick={() => scrollTo("contacto")}>
              {t("landing.requestDemo")}
            </Button>
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <LanguageSwitcher variant="landing" />
            <button className="text-foreground" onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {isOpen && (
          <div className="md:hidden bg-background/95 backdrop-blur-2xl border-t border-border/30 p-6 space-y-4">
            {navLinks.map(([label, id]) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className="block w-full text-left text-muted-foreground hover:text-accent text-sm font-mono uppercase tracking-widest py-2 transition-colors"
              >
                {label}
              </button>
            ))}
            <Button variant="outline" className="w-full" onClick={() => { setIsOpen(false); setLoginOpen(true); }}>
              <LogIn className="h-4 w-4 mr-2" /> {t("landing.accessPlatform")}
            </Button>
            <Button variant="hero" className="w-full shimmer-btn" onClick={() => scrollTo("contacto")}>
              {t("landing.requestDemo")}
            </Button>
          </div>
        )}
      </nav>

      {loginOpen && (
        <Suspense fallback={null}>
          <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
        </Suspense>
      )}
    </>
  );
};

export default Navbar;
